import React, { useState, useEffect, useRef } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "../components/ui/tooltip";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import VoiceFillButton from "./VoiceFillButton";
import api, { errMsg } from "../api";
import { toast } from "sonner";
import { Paperclip, X as XIcon, Info, Loader2 } from "lucide-react";

const ACCEPTED_FORMATS = ["JPG", "PNG", "GIF", "WEBP", "PDF", "DOCX", "PPTX", "XLSX", "CSV", "TXT"];

export default function NewTaskDialog({ open, onClose, onCreated, spaces, users, defaultSpaceId }) {
    const { lang } = useAuth();
    const [spaceId, setSpaceId] = useState(defaultSpaceId || "");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [deadline, setDeadline] = useState("");
    const [priority, setPriority] = useState("medium");
    const [assignees, setAssignees] = useState([]);
    const [assignedToSpace, setAssignedToSpace] = useState(false);
    const [isPrivate, setIsPrivate] = useState(false);
    const [price, setPrice] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    useEffect(() => {
        // Reset form state ONLY when dialog transitions from closed → open.
        // Earlier this fired on every `spaces` re-render which wiped what the user typed.
        if (!open) return;
        setSpaceId(defaultSpaceId || (spaces[0]?.id || ""));
        setTitle(""); setDescription(""); setDeadline(""); setPriority("medium");
        setAssignees([]); setPrice(""); setCurrency("EUR"); setFiles([]);
        setAssignedToSpace(false);
        setIsPrivate(false);
        setUploadProgress(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const space = spaces.find((s) => s.id === spaceId);
    // Show ALL users (not just space members) so admin can assign external helpers as guests.
    const eligibleUsers = (() => {
        if (!space) return users;
        const memberIds = new Set(space.member_ids || []);
        const members = users.filter((u) => memberIds.has(u.id) || u.role === "admin");
        const externals = users.filter((u) => !memberIds.has(u.id) && u.role !== "admin");
        return [...members, ...externals];
    })();
    const isGuestUser = (uid) => space && !(space.member_ids || []).includes(uid) && users.find((u) => u.id === uid)?.role !== "admin";

    const addFiles = (e) => {
        const list = Array.from(e.target.files || []);
        setFiles((prev) => [...prev, ...list]);
        e.target.value = "";
    };
    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const submit = async () => {
        if (!title.trim()) return;
        if (!isPrivate && !spaceId) return;
        setBusy(true);
        try {
            const priceNum = price === "" ? null : parseFloat(price);
            // Private todo path
            if (isPrivate) {
                await api.post("/todos", {
                    title,
                    description,
                    deadline: deadline ? new Date(deadline).toISOString() : null,
                    priority,
                });
                if (onCreated) onCreated();
                onClose();
                return;
            }
            const payload = {
                space_id: spaceId,
                title,
                description,
                deadline: deadline ? new Date(deadline).toISOString() : null,
                priority,
                assignee_ids: assignees,
                assigned_to_space: assignedToSpace,
                price: Number.isFinite(priceNum) ? priceNum : null,
                currency: priceNum != null ? currency : null,
            };
            const { data: task } = await api.post("/tasks", payload);

            // Upload attachments if any
            if (files.length > 0) {
                let done = 0;
                for (const f of files) {
                    const form = new FormData();
                    form.append("file", f);
                    try {
                        await api.post(`/tasks/${task.id}/files`, form, {
                            headers: { "Content-Type": "multipart/form-data" },
                            onUploadProgress: (evt) => {
                                if (evt.total) {
                                    const single = (evt.loaded / evt.total) * 100;
                                    const total = ((done + single / 100) / files.length) * 100;
                                    setUploadProgress(Math.round(total));
                                }
                            },
                        });
                    } catch (err) {
                        toast.error(`${f.name}: ${errMsg(err, "upload failed")}`);
                    }
                    done++;
                    setUploadProgress(Math.round((done / files.length) * 100));
                }
            }

            toast.success(lang === "tr" ? "Görev oluşturuldu" : lang === "de" ? "Aufgabe erstellt" : "Task created");
            onCreated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
            setUploadProgress(0);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-task-dialog">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <DialogTitle className="font-display text-2xl tracking-tight">{t(lang, "newTask")}</DialogTitle>
                        <VoiceFillButton
                            formType="task"
                            lang={lang}
                            onParsed={(f) => {
                                if (f.title) setTitle(String(f.title));
                                if (f.description) setDescription(String(f.description));
                                if (f.priority && ["low","medium","high"].includes(f.priority)) setPriority(f.priority);
                                if (f.deadline) setDeadline(String(f.deadline));
                                if (f.price != null && !Number.isNaN(Number(f.price))) setPrice(String(f.price));
                                if (f.space_hint) {
                                    const hint = String(f.space_hint).toLowerCase();
                                    const m = spaces.find((s) => s.name.toLowerCase().includes(hint));
                                    if (m) setSpaceId(m.id);
                                }
                            }}
                        />
                    </div>
                    <DialogDescription className="text-xs text-neutral-500">
                        {lang === "tr" ? "Oluşturma tarihi otomatik olarak şimdi atanacak" : lang === "de" ? "Das Erstellungsdatum wird automatisch auf jetzt gesetzt" : "Creation timestamp will be set to now automatically"}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3.5">
                    <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                        <input
                            type="checkbox"
                            checked={isPrivate}
                            onChange={(e) => setIsPrivate(e.target.checked)}
                            data-testid="new-task-private"
                            className="w-4 h-4"
                        />
                        <span className="flex items-center gap-1.5 text-emerald-900">
                            <span className="font-semibold">🔒 {lang === "de" ? "Private Aufgabe (nur ich sehe sie)" : lang === "tr" ? "Özel görev" : "Private task (only I see it)"}</span>
                        </span>
                    </label>
                    {!isPrivate && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "spaces")}</Label>
                            <Select value={spaceId} onValueChange={setSpaceId}>
                                <SelectTrigger className="mt-1.5" data-testid="new-task-space-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {spaces.map((s) => (
                                        <SelectItem key={s.id} value={s.id} data-testid={`new-task-space-opt-${s.id}`}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "title")}</Label>
                        <Input data-testid="new-task-title" className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "description")}</Label>
                        <Textarea data-testid="new-task-desc" className="mt-1.5" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "deadline")}</Label>
                            <Input type="datetime-local" data-testid="new-task-deadline" className="mt-1.5" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "priority")}</Label>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger className="mt-1.5" data-testid="new-task-priority"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">{t(lang, "low")}</SelectItem>
                                    <SelectItem value="medium">{t(lang, "medium")}</SelectItem>
                                    <SelectItem value="high">{t(lang, "high")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Price + Currency */}
                    <div className="grid grid-cols-[1fr,110px] gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                                {t(lang, "price")} <span className="font-normal lowercase text-neutral-400">({lang === "tr" ? "opsiyonel" : lang === "de" ? "optional" : "optional"})</span>
                            </Label>
                            <Input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                data-testid="new-task-price"
                                className="mt-1.5"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "currency")}</Label>
                            <div className="mt-1.5 h-10 rounded-md border border-neutral-200 bg-neutral-50 px-3 flex items-center font-medium text-sm" data-testid="new-task-currency">€ EUR</div>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">{t(lang, "assignees")}</Label>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {eligibleUsers.map((u) => {
                                const active = assignees.includes(u.id);
                                const isGuest = isGuestUser(u.id);
                                return (
                                    <button
                                        key={u.id}
                                        type="button"
                                        data-testid={`new-task-assignee-${u.id}`}
                                        onClick={() => setAssignees((prev) => prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id])}
                                        title={isGuest ? (lang === "de" ? "Nicht in dieser Abteilung — wird als Gast zugewiesen" : "Not in this department — assigned as guest") : undefined}
                                        className={`px-2 py-1 rounded-md text-xs border flex items-center gap-1 ${
                                            active
                                                ? (isGuest ? "bg-amber-600 text-white border-amber-700" : "bg-neutral-900 text-white border-neutral-900")
                                                : (isGuest ? "border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100")
                                        }`}
                                    >
                                        {u.name}
                                        {isGuest && <span className="text-[9px] font-bold opacity-80">Gast</span>}
                                    </button>
                                );
                            })}
                            {eligibleUsers.length === 0 && (
                                <p className="text-xs text-neutral-400 italic">—</p>
                            )}
                        </div>
                        <p className="text-[10px] text-neutral-500 mt-1">
                            {lang === "de" ? "💡 Tipp: Auch externe Mitarbeiter (z.B. IT) können als Gast zugewiesen werden." : "💡 External members can be assigned as guests."}
                        </p>
                        <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer p-2 bg-blue-50 border border-blue-200 rounded-md">
                            <input
                                type="checkbox"
                                checked={assignedToSpace}
                                onChange={(e) => setAssignedToSpace(e.target.checked)}
                                data-testid="new-task-assigned-to-space"
                                className="w-4 h-4"
                            />
                            <span className="flex items-center gap-1.5 text-blue-900">
                                <span className="font-semibold">{t(lang, "assignToSpace")}</span>
                                <span className="text-[10px] text-blue-700/80">
                                    ({lang === "de" ? "alle Abteilungs-Mitglieder sehen, einer kann übernehmen" : lang === "tr" ? "tüm üyeler görür, biri üstlenir" : "all members see, one can claim"})
                                </span>
                            </span>
                        </label>
                    </div>

                    {/* Attachments */}
                    <div>
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                                {t(lang, "attachments")} {files.length > 0 && <span className="text-neutral-500 font-normal">({files.length})</span>}
                            </Label>
                            <div className="flex items-center gap-2">
                                <TooltipProvider delayDuration={150}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" data-testid="new-task-upload-info" className="text-neutral-500 hover:text-neutral-900">
                                                <Info className="w-3.5 h-3.5" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs">
                                            <p className="text-xs">
                                                <span className="font-semibold">{t(lang, "acceptedFormats")}:</span>{" "}
                                                {ACCEPTED_FORMATS.join(", ")}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="new-task-add-files"
                                    className="text-xs flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-300 hover:bg-neutral-50"
                                >
                                    <Paperclip className="w-3 h-3" /> {t(lang, "attachFiles")}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    hidden
                                    multiple
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.pptx,.xlsx,.csv,.txt"
                                    onChange={addFiles}
                                    data-testid="new-task-file-input"
                                />
                            </div>
                        </div>
                        {files.length > 0 && (
                            <div className="mt-1.5 space-y-1">
                                {files.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 p-1.5 rounded-md border border-neutral-200 bg-neutral-50/60 text-xs" data-testid={`new-task-file-${i}`}>
                                        <Paperclip className="w-3 h-3 text-neutral-500 shrink-0" />
                                        <span className="flex-1 truncate">{f.name}</span>
                                        <span className="text-neutral-500 text-[10px]">{(f.size / 1024).toFixed(1)} KB</span>
                                        <button onClick={() => removeFile(i)} className="text-neutral-500 hover:text-red-600" data-testid={`new-task-file-remove-${i}`}>
                                            <XIcon className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {busy && files.length > 0 && (
                            <div className="mt-2">
                                <div className="h-1 bg-neutral-100 rounded overflow-hidden" data-testid="new-task-upload-progress">
                                    <div className="h-full bg-neutral-900 transition-all" style={{ width: `${uploadProgress}%` }} />
                                </div>
                                <p className="text-[10px] text-neutral-500 mt-0.5">{uploadProgress}%</p>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="new-task-cancel" disabled={busy}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !title.trim() || (!isPrivate && !spaceId)} data-testid="new-task-create" className="bg-neutral-900 hover:bg-neutral-800">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
